import React, { useState } from "react";
import { Card, Box,  Typography, Stack,  useTheme, Avatar } from "@mui/material";
import { Team } from "../../../types/teams.types";
import { UserIcon } from "@heroicons/react/24/solid";
import { rgba } from "polished";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";
import { TeamService } from "../../../services/team.service";
import { useNavigate } from "react-router-dom";
import { Tag } from "../../../types/tags.types";
interface TeamCardProps {
    team: Team;
}

const TeamCard: React.FC<TeamCardProps> = ({ team }) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const {
        items: availableTags,
    } = useSelector((state: RootState) => state.tags);

    const tagsMap: Record<string, typeof availableTags[number]> = {};

    availableTags.forEach((t: Tag) => {
        tagsMap[t._id!] = t;
    });


    return (
        <Card
            onClick={() => {
                navigate(`/teams/${team.id}/edit`)
            }} 
            sx={{
                display: "flex",
                flexDirection: "column",
                textAlign: "center",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1,
                mt: 1,
                mb: 1,
                m: 1,
                width: "98%",
                backgroundColor: "white",
                height: "30vh",
                cursor: "pointer",
                "&:hover": {
                    backgroundColor: "whitesmoke",
                },
            }}
        >
            {/* Team Details */}
            <Box zIndex={-10}>
                <Typography
                    fontSize={26}
                    fontWeight={400}
                    sx={{ color: "rgba(99, 115, 129, 1)" }}
                >
                    {team.name}
                </Typography>
                <Typography
                    fontSize={12}
                    fontWeight={600}
                    color="textSecondary"
                    sx={{ color: "rgba(196, 205, 213, 1)" }}
                >
                    {team.location}
                </Typography>

                {/* Team Tags (Uncomment if needed) */}
                <Stack direction="row" spacing={1} rowGap={1} mt={1} flexWrap="wrap" justifyContent="center">
                    {team.tags?.map((tag, tagIdx) => (
                        <Box
                            key={tagIdx}
                            sx={{
                                backgroundColor: tagsMap[tag]?.color,
                                padding: "4px 8px",
                                borderRadius: "8px",
                                fontSize: "12px",
                                color: "white"
                            }}
                        >
                            {tagsMap[tag]?.name}
                        </Box>
                    ))}
                </Stack>

                {/* User Count */}
                <UserGroupDisplay memberCount={team.users?.length ?? 0} />
            </Box>
        </Card>
    );
};

const UserGroupDisplay = ({ memberCount = 0 }) => {
    const avatarSize = 25;
    const overlap = avatarSize - 8;
    const displayedUsers = Math.min(memberCount, 3);
    const extraUsers = memberCount > 3 ? memberCount - 3 : 0;
    const theme = useTheme();
    const colorButton = theme.palette.primary.main;
    const totalWidth = displayedUsers * overlap + (extraUsers > 0 ? avatarSize : 0);
    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent='center'
            width="100%"
            height={avatarSize}
            position="relative"

            mt={2}
        >
            <Box
                width="100%"
                sx={{
                    position: "absolute",
                    left: "45%",
                    transform: `translateX(-${totalWidth / 2}px)`,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                {[...Array(displayedUsers)].map((_, index) => (
                    <Avatar
                        key={index}
                        sx={{
                            width: avatarSize,
                            height: avatarSize,
                            border: "1px solid white",
                            position: "absolute",
                            left: `${index * overlap}px`,
                            zIndex: 0 - index,
                        }}
                    >
                        <UserIcon fontSize="small" />
                    </Avatar>
                ))}
                {extraUsers > 0 && (
                    <Avatar
                        sx={{
                            width: avatarSize,
                            height: avatarSize,
                            bgcolor: colorButton,
                            color: "white",
                            position: "absolute",
                            border: "1px solid white",
                            left: `${displayedUsers * overlap}px`,
                            zIndex: 0 - displayedUsers,
                        }}
                    >
                        <Typography fontSize={8}>+{extraUsers}</Typography>
                    </Avatar>
                )}
                <Typography
                    sx={{
                        position: "absolute",
                        left: `${(displayedUsers + 2) * overlap}px`,
                        color: rgba(99, 115, 129, 1)
                    }}
                    fontSize={8}
                >
                    {memberCount} members
                </Typography>
            </Box>

        </Box>
    );
};


export default TeamCard;
